import { SyntheticEvent, useEffect, useState } from 'react';

import { Autocomplete, FormControl, TextField } from '@mui/material';
import { toast } from 'react-toastify';

import { APIErrorT } from 'shared/models/api';
import { APICallParams, APIInterfaceI, PaginationT } from 'utils/api';
import { generateRandomString } from 'utils/string';

export interface InputAutocompleteOption {
  label: string;
  groupBy?: string;
  id: string;
}

interface InputAutocompleteProps {
  autocompleteName: string;
  grouped?: boolean;
  optionFormatFn: Function;
  apiCall: APIInterfaceI;
  apiParams?: APICallParams;
  onChangeFn: Function;
  required?: boolean;
  defaultValue?: string;
}
function InputAutocomplete({
  autocompleteName,
  optionFormatFn,
  apiCall,
  apiParams,
  onChangeFn,
  required = false,
  grouped = false,
  defaultValue,
}: InputAutocompleteProps) {
  const [list, setList] = useState<any[]>([]);

  const loadDynamicList = () => {
    apiCall.getAll(
      {
        cursor: 0, limit: 1000,
      } as PaginationT,
      apiParams,
    )
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
            });
        } else {
          resp.json()
            .then((data: any) => {
              let options = data.result
                .map((option: any): InputAutocompleteOption => optionFormatFn(option));
              if (grouped) {
                options = options.sort((a: any, b: any) => -b.groupBy.localeCompare(a.groupBy));
              }
              setList(options);
            });
        }
      });
  };

  useEffect(() => {
    loadDynamicList();
  }, []);

  const onChange = (
    _: SyntheticEvent<Element, Event>,
    value: any | null,
  ) => onChangeFn(value);

  return (
    <FormControl fullWidth sx={{ m: 1 }}>
      <Autocomplete
        id={`grouped-${autocompleteName}s`}
        options={list}
        groupBy={grouped ? (option) => option.groupBy : undefined}
        getOptionLabel={grouped ? (option) => `${option.groupBy} - ${option.label}` : undefined}
        isOptionEqualToValue={
          (option: InputAutocompleteOption, value: any) => (
            option.label === value || option.label === value.label
          )
        }
        onChange={onChange}
        renderOption={(props, option) => (
          <li
            {...props}
            key={generateRandomString(3)}
          >
            {option.label}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            required={required}
            label={`Select your ${autocompleteName}`}
            InputLabelProps={{
              required: false, // Disable asterisk * on required
            }}
          />
        )}
        defaultValue={defaultValue}
      />
    </FormControl>
  );
}

export default InputAutocomplete;
